import { StripeError } from "@stripe/stripe-js";

declare global {
  interface Window {
    Cypress?: Cypress.Cypress;
    __urlToUpdateFromEmail__: string;
  }
}

export enum CommericalAction {
  add = "add",
  remove = "remove",
}

export enum Periods {
  lunch = "lunch",
  dinner = "dinner",
  afternoon = "afternoon",
  breakfast = "breakfast",
  brunch = "brunch",
}

export interface PeriodsTranslations {
  period: string;
  translations: Array<PeriodTranslations>;
}

export interface PeriodTranslations {
  locale: string;
  renamingPeriod: string;
}

export const Gender = {
  male: 1,
  female: 2,
  male_and_female: 3,
};

export const periodsArray: Array<string> = Object.values(Periods);

export enum BookingState {
  initialized = "initialized",
  waiting = "waiting",
  validated = "validated",
  canceled = "canceled",
}

export enum AvailableCommercials {
  menus = "menus",
  offers = "offers",
}

export enum SelectionSteps {
  guests = "guests",
  date = "date",
  area = "area",
  period = "period",
  time = "time",
  offer = "offer",
  menu = "menu",
}

export enum DayStatus {
  waiting = "waiting",
  closed = "closed",
  available = "available",
  full = "full",
  unavailable = "unavailable",
}

export enum EngineMode {
  capacity = "capacitive",
  table = "table",
}

export enum ButtonStyle {
  text = "text",
  contained = "contained",
  outlined = "outlined",
}

export interface IStyle {
  disabled?: boolean;
  isSummary?: boolean;
  inverted?: boolean;
}

export interface IFromStyle {
  minHeight?: string;
}

export enum HeaderStyle {
  text = "text",
  contained = "contained",
}

export interface DailyLightInfo {
  date: string;
  status: DayStatus;
  engine_mode: EngineMode;
}

export interface DailyLightInfoResponse {
  daily_availabilities: Array<DailyLightInfo>;
}

export interface GuestsCapacity {
  minGuests: number;
  maxGuests: number;
}

export enum CommercialKeys {
  menus = "menu",
  offers = "offer",
  events = "event",
}

export interface BaseInterface {
  object: string;
}

export interface Area extends BaseInterface {
  id: number;
  bookable_online: boolean;
  translations: Array<AreaTranslation>;
}

export interface AreaTranslation extends BaseInterface {
  locale: string;
  name: string;
}

export interface RestaurantBookingDetail extends BaseInterface {
  encoded_note: { [key: string]: string };
  note: { [key: string]: string };
}

export interface Period extends BaseInterface {
  area_id?: number;
  booking_details: Array<BookingDetail>;
  date: string;
  hours: Array<Hour>;
  period: Periods;
  persons: number;
  restaurant_id: number;
  restaurant_name: string;
  total_left_seats: number;
  no_hour_reason: string;
  non_bookable_event: NonBookableEvent;
}

export interface NonBookableEvent {
  event_id: string;
  event_name: string;
  event_description: string;
}

export interface PeriodsResponse {
  periods: Array<Period>;
  next_available_date?: string;
  message?: string;
}

export interface PeriodsRequest {
  date: string;
  persons: number;
  restaurant_id?: number;
  area_id?: number;
  period?: Periods;
  expand?: any;
}

export interface Hour extends BaseInterface {
  bookable_area_ids: Array<number>;
  booking_state: "validated" | "initialized" | "waiting";
  event: any;
  waitingList?: boolean;
  offers: Array<any>;
  time: string;
  has_prepayment: boolean;
}

export interface BookingDetail extends BaseInterface {
  encoded_note: Array<BookingDetailTranslation>;
  note: Array<BookingDetailTranslation>;
}

export interface BookingDetailTranslation {
  [key: string]: string;
}

export interface InitialPendingBookingRequest {
  area_id?: string;
  date: string;
  hour: string;
  persons: number;
  children: number;
  restaurant_id: number;
  booking_token?: string;
  contact_info?: PendingBookingContact;
  metadata?: any;
  user_locale?: string;
  note?: string;
}

export interface PendingBooking extends BaseInterface {
  note?: string;
  area_id?: null;
  available_commercials: Array<Commercial>;
  available_menus: Array<Menu>;
  booking_state: BookingState;
  bookings_detail?: RestaurantBookingDetail;
  commitable: boolean;
  contact_info: PendingBookingContact;
  date: string;
  hour: string;
  id: string;
  menu_selection_requested?: boolean;
  same_number_menu?: boolean;
  metadata: any;
  event?: CommercialLight;
  offer: CommercialLight;
  opening_id?: string;
  optional: boolean;
  persons: number;
  children: number;
  prepayment: Prepayment;
  restaurant_id: number;
  selected_menus?: Array<SelectedMenu>;
  user_locale?: string;
  voucher_enabled: boolean;
  allow_vouchers: boolean;
  voucher_infos?: any;
  vouchers: Array<Voucher>;
  country_code?: string;
  skip_prepayment_for_contact?: boolean;
  recaptchaValue?: string;
}

export interface PendingBookingContact {
  lastname: string;
  firstname: string;
  mobile_phone: string;
  country_code: string;
  country?: string;
  address?: string;
  extra_address?: string;
  postcode?: string;
  city?: string;
  email: string;
  message?: string;
  gender?: number;
  birthday?: string;
  company?: string;
  allergies?: string;
  accept_sms_spam?: boolean;
  accept_email_spam?: boolean;
}
export interface Contact extends BaseInterface {
  email: string;
  lastname: string;
  firstname: string;
  restaurant_id: number;
}
export interface Booking extends BaseInterface {
  id: number;
  booking_state: BookingState;
  booked_persons_number: number;
  children_number: number | 0;
  present_persons_number: number;
  cancel_actor: null;
  cancel_reason: null;
  booking_type: string;
  note: null;
  period: Periods;
  booking_date: string;
  booking_time: string;
  booking_token: string;
  email: string;
  firstname: string;
  lastname: string;
  send_reminders: boolean;
  user_locale: string;
  metadata: any;
  contact_id: number;
  restaurant_id: number;
  waiting_rank: number;
  civility?: number;
  rr_data?: RRData;
}

export interface Prepayment {
  client_secret: string;
  intent_id: string;
  stripe_account: any;
  kind: string;
  amount: number;
  stripe_key: string;
  payment_kind: string;
  currency: string;
  origin?: string;
}

export interface PrepaymentInfos {
  currency: string;
  kind: string;
  parent_id: string;
  payment_kind: string;
  price: number;
}

export interface CommercialLight extends BaseInterface {
  commercial_id: number;
}

export interface QueryParam {
  queryParamName: string;
  queryParamValue: string;
}

export enum CommercialsObject {
  menu = "menu",
  event = "event",
  offer = "offer",
}

export interface CommercialsResponse {
  commercials: Array<Commercial>;
  current_page: number;
  num_pages: number;
  total_count: number;
  total_pages: number;
}

export interface PaymentSecretResponse {
  secret: string;
}

export interface Commercial extends BaseInterface {
  bookable: boolean;
  commercial_recurrencies: Array<CommercialRecurrence>;
  description: string;
  end_date: string | null;
  end_time: string | null;
  has_prepayment: boolean;
  id: number;
  maximum_dishes: number;
  minimum_dishes: number;
  name: string;
  permanent: boolean;
  restaurant_id: number;
  start_date: any | null;
  start_time: string | null;
  translations: Array<CommercialTranslation>;
  image: string;
  prepayment?: PrepaymentInfos;
}

export interface Menu extends Commercial {
  left: number;
  for_all_guests?: boolean;
}

export interface CommercialTranslation extends BaseInterface {
  locale: string;
  name: string;
  description: string;
}

export interface CommercialRecurrence extends BaseInterface {
  end_hour: string;
  month_week: number;
  start_hour: string;
  week_day: number;
}

export interface CustomContactFormFields {
  accept_partner_spam: boolean;
  allow_blank_email: boolean;
  show_birthday: boolean;
  show_gender: boolean;
  show_company: boolean;
  show_tol_spam: boolean;
  show_address: boolean;
  show_country: boolean;
  show_sharing_friend_on_module: boolean;
  show_allergies_terms_on_module: boolean;
}

export interface UrlParams {
  referralKey: string;
  version?: string;
  restaurantId: string;
  bookingId?: string;
}

export interface IThemeProps {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  headerBackgroundColor?: string;
  bodyBackgroundColor?: string;
  headerStyle?: HeaderStyle;
  textColor?: string;
  textColorGray?: string;
  disabledColor?: string;
  success?: string;
  buttonStyle?: ButtonStyle;
}

export interface AdvancedOptions {
  select_only_pax_first: boolean;
  reservation_responsable_enabled: boolean;
  force_area_selection: boolean;
  hide_filter_selection: boolean;
  has_group_skipping_prepayment?: boolean;
  recaptcha_enabled?: boolean;
}
export interface WaitingMessages {
  translations: Array<WaitingMessage>;
}
export interface WaitingMessage {
  locale: string;
  restaurant_message: string | null;
  default_message: string | null;
}

export interface PoweredBy {
  logo: string;
  powered_by_texts: Translations;
  url: string;
}

export interface Translations {
  translations: Array<Translation>;
}
export interface Translation {
  locale: string;
  text: string;
}

export interface ModuleSettings extends BaseInterface, CustomContactFormFields {
  google_font: string;
  available_locales: Array<string>;
  callback_url?: string | null;
  restaurant: SettingsRestaurant;
  show_powered_by: boolean;
  side_picture_url: string;
  banner_url: string;
  redirection_link: string;
  referral_key: string;
  update_from_booking: UpdateFromBooking;
  terms?: Terms;
  partner_display_name: string;
  theme: IThemeProps;
  product_name: string;
  external_partner?: ExternalPartner;
  advanced_options: AdvancedOptions;
  optional_contact_form_fields: Array<string>;
  background_picture_url: string;
  callFromFloatingButton: boolean;
  force_area_selection?: boolean;
  waiting_messages: WaitingMessages;
  powered_by: PoweredBy;
}

export interface ErrorMessage {
  locale: string;
  error_message: string;
}

export interface UpdateFromBooking {
  area_id: string | null;
  booked_persons_number: number | null;
  booking_date: string | null;
  booking_time: string | null;
  children_number: number | null;
  error_message: Array<ErrorMessage>;
}

export interface ExternalPartner {
  facebook_pixel_id?: string;
}

export interface RRData {
  title: string | undefined;
  text: string | undefined;
}

export interface SettingsRestaurant extends BaseInterface {
  areas: Array<Area>;
  bookings_detail: RestaurantBookingDetail;
  capacity: number;
  events: Array<any>;
  first_step: string;
  id: number;
  time_zone: string;
  imageUrl?: string;
  last_booking_date: string;
  locale: string;
  max_persons: number;
  menus: Array<any>;
  min_persons: number;
  default_persons: number;
  name: string;
  offers: Array<any>;
  periods: Array<any>;
  periods_translations: Array<PeriodsTranslations>;
  online_booking_enabled: boolean;
  allow_multi_vouchers: boolean;
  delay_before_deposit_capture: number;
  display_allergies_field: boolean;
  children?: Children;
  country_code?: string;
}

export interface Children {
  children_management_enabled: boolean;
  children_max_age: number;
}

export interface Terms {
  disclaimer_url: string;
  prepayment_disclaimer_url: string;
  privacy_policy_url: string;
}

export interface IInputWithIcon {
  error: boolean;
  value: any;
  isNumeric?: boolean;
  label?: string;
  placeholderKey?: string;
  variant?: any;
  helperText?: string;
  onChange?: any;
  onClick?: any;
  icon: any;
  isSummary?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  name?: string;
  dataTest?: string;
}

export interface ISlot {
  value: string;
  isSelected?: boolean;
  onClick?: any;
  icons?: ISlotIcons;
}

export interface ISlotIcons {
  hasWaitingList?: boolean;
  hasOffer?: boolean;
  hasPrepayment?: boolean;
}

export interface IDisplayPeriod {
  titleKey: string;
  hours?: Array<ISlot>;
  noHourReason?: string;
  nonBookableEvent?: NonBookableEvent;
}

export interface IDisplayAvailablePeriods {
  displayPeriods: IDisplayPeriod[];
  filters: any;
  message?: string;
}

export interface DropdownOption {
  value: any;
  displayText: string;
  isSelected?: boolean;
}

export interface NormalizedOption extends DropdownOption {
  normalized?: string;
}

export interface TagManagerOptions {
  first_step?: boolean;
  booking_id?: string;
  pending_booking_id?: string;
  guests?: number;
}

export interface GolReducer {
  observeProps: Array<string>;
  reducer: () => void;
}

export interface GolError {
  errorMessage: Array<string>;
  field: string;
}

export interface GolResourceError {
  resource: string;
  errors: Array<GolError>;
}

export interface GolStripeCallbackResponse {
  stripeToken?: string;
  stripeError?: StripeError;
}

export interface SelectedMenu extends BaseInterface {
  name: string;
  commercial_id: string;
  quantity: number;
  currency: string;
  unit_price: number;
  image?: string;
  for_all_guests?: boolean;
}

export interface WarrantVoucher extends BaseInterface {
  name: string;
  description: string;
  status: string;
  currency: string;
}

export interface GiftVoucher extends WarrantVoucher {
  amount: number;
}

export interface MenuSelection {
  menuId: number;
  count: number;
  for_all_guests?: boolean;
}

export interface SelectedOffer extends Commercial {
  isSelected: boolean;
}

export interface Voucher {
  amount: number;
  code: string;
  description: string;
  name: string;
  status: string;
  type: string;
  voucher_type: string;
}

export enum ExtraParams {
  pax = "pax",
  persons = "persons",
  date = "date",
  time = "time",
  locale = "locale",
  area_id = "area_id",
  note = "note",
  last_name = "contact_infos[lastname]",
  first_name = "contact_infos[firstname]",
  email = "contact_infos[email]",
  country_code = "contact_infos[country_code]",
  mobile_phone = "contact_infos[mobile_phone]",
  background_color = "background_color",
  hide_general_terms = "hide_general_terms",
  axeptio_token = "axeptio_token",
  ga = "ga",
}
export interface OverwrittenParams {
  readonly pax?: string;
  readonly persons?: string;
  readonly date?: string;
  readonly time?: string;
  readonly locale?: string;
  readonly area_id?: string;
  readonly note?: string;
  readonly last_name?: string;
  readonly first_name?: string;
  readonly email?: string;
  readonly country_code?: string;
  readonly mobile_phone?: string;
  readonly background_color?: string;
  readonly hide_general_terms?: string;
  readonly axeptio_token?: string;
  readonly ga?: string;
}

export interface ICommercialsMenuItemProps {
  menu: Menu;
  setChildLoading: (state: boolean) => void;
}

export interface IDisabled {
  disabled?: boolean;
}
